import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  automationService,
  type Strategy,
  type Scenario,
  type ScenarioSummary,
  type RuleStatistics,
  type StrategyTrade,
} from '@/services/strategyService';
import { EquityCurveChart, PnLDistributionChart, MfeScatterPlot } from './PerformanceCharts';
import {
  Play,
  Trash2,
  RefreshCw,
  Clock,
  BarChart3,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Loader2,
} from 'lucide-react';

const fmt = (n: number, digits = 2) => n.toFixed(digits);
const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
const fmtDollar = (n: number) => `${n >= 0 ? '+' : ''}$${n.toFixed(2)}`;
const fmtDuration = (secs: number) => {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
};
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString() : '—';

export const ScenarioBacktestPage: React.FC = () => {
  const [rules, setRules] = useState<Strategy[]>([]);
  // List endpoint returns lightweight summaries (no trades). Full Scenario is
  // lazy-fetched into selectedScenarioFull when the user selects one.
  const [scenarios, setScenarios] = useState<ScenarioSummary[]>([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  const [selectedScenarioFull, setSelectedScenarioFull] = useState<Scenario | null>(null);
  const [selectedRuleId, setSelectedRuleId] = useState<string>('');
  const [lookbackDays, setLookbackDays] = useState(90);
  const [symbolsInput, setSymbolsInput] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        automationService.getRules(),
        automationService.listScenarios(20),
      ]);
      setRules(r);
      setScenarios(s);
      if (r.length > 0 && !selectedRuleId) setSelectedRuleId(r[0].id);
    } catch (e: any) {
      setError(e.message);
    }
  }, [selectedRuleId]);

  useEffect(() => { load(); }, [load]);

  // Lazy-fetch the full scenario (with trades) only when one is selected.
  useEffect(() => {
    if (!selectedScenarioId) { setSelectedScenarioFull(null); return; }
    let cancelled = false;
    automationService.getScenario(selectedScenarioId)
      .then(s => { if (!cancelled) setSelectedScenarioFull(s); })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [selectedScenarioId]);

  const runScenario = async () => {
    if (!selectedRuleId) return;
    setRunning(true);
    setError(null);
    try {
      const symbols = symbolsInput.trim()
        ? symbolsInput.split(',').map(s => s.trim().toUpperCase()).filter(Boolean)
        : undefined;
      const result = await automationService.runScenario(selectedRuleId, lookbackDays, symbols);
      setScenarios(prev => [result, ...prev]);
      setSelectedScenarioId(result.id);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const deleteScenario = async (id: string) => {
    if (!confirm('Delete this scenario?')) return;
    try {
      await automationService.deleteScenario(id);
      setScenarios(prev => prev.filter(s => s.id !== id));
      if (selectedScenarioId === id) setSelectedScenarioId(null);
    } catch (e: any) {
      setError(e.message);
    }
  };

  // selectedScenario is the full Scenario (with trades) when loaded; falls back
  // to the summary while the lazy fetch is in flight so stats show immediately.
  // backtestSessionId moved into the nested backtest object in slice 4;
  // the summary already carries `backtest`, so we just spread it through.
  const selectedSummary = scenarios.find(s => s.id === selectedScenarioId);
  const selectedScenario: Scenario | null = selectedScenarioFull
    ?? (selectedSummary ? { ...selectedSummary, trades: [] } : null);
  const stats = selectedScenario?.statistics;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <BarChart3 className="h-4 w-4 text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">Scenario Backtest</h2>
          <span className="text-xs text-muted-foreground">Test scanner + strategy combinations over historical data</span>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">{error}</div>
      )}

      {/* ── Run Form ── */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Automation Rule</label>
              <select
                className="h-9 px-3 rounded-md border bg-background text-sm min-w-[200px]"
                value={selectedRuleId}
                onChange={e => setSelectedRuleId(e.target.value)}
              >
                {rules.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Lookback Days</label>
              <input
                type="number"
                className="h-9 w-24 px-3 rounded-md border bg-background text-sm"
                value={lookbackDays}
                onChange={e => setLookbackDays(Number(e.target.value))}
                min={1}
                max={365}
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground block mb-1">Symbols (comma-separated, blank = all)</label>
              <input
                type="text"
                className="h-9 w-full px-3 rounded-md border bg-background text-sm"
                placeholder="AAPL, TSLA, NVDA"
                value={symbolsInput}
                onChange={e => setSymbolsInput(e.target.value)}
              />
            </div>
            <Button size="sm" onClick={runScenario} disabled={running || !selectedRuleId}>
              {running ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
              {running ? 'Running...' : 'Run Scenario'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* ── Scenario List ── */}
        <div className="space-y-2 max-h-[600px] overflow-auto">
          {scenarios.map(s => (
            <Card
              key={s.id}
              className={`cursor-pointer transition-colors ${selectedScenarioId === s.id ? 'border-primary' : 'hover:border-muted-foreground/30'}`}
              onClick={() => setSelectedScenarioId(s.id)}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium truncate mr-2">{s.name}</span>
                  <div className="flex items-center gap-1">
                    <StatusBadge status={s.status} />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-destructive"
                      onClick={e => { e.stopPropagation(); deleteScenario(s.id); }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {fmtDate(s.backtest?.startDate ?? '')} → {fmtDate(s.backtest?.endDate ?? '')} | {s.tradeCount ?? 0} trades
                  {s.statistics && <span className={s.statistics.totalPnL >= 0 ? ' text-green-500' : ' text-red-500'}>
                    {' '}| {fmtDollar(s.statistics.totalPnL)}
                  </span>}
                </div>
              </CardContent>
            </Card>
          ))}
          {scenarios.length === 0 && (
            <div className="text-center text-muted-foreground text-sm py-6">
              No scenarios yet. Run your first one above.
            </div>
          )}
        </div>

        {/* ── Results Panel ── */}
        <div className="xl:col-span-2 space-y-4">
          {selectedScenario && stats ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Total Trades" value={stats.totalTrades.toString()} />
                <StatCard label="Win Rate" value={fmtPct(stats.winRate)} positive={stats.winRate >= 50} />
                <StatCard label="Total P&L" value={fmtDollar(stats.totalPnL)} positive={stats.totalPnL >= 0} />
                <StatCard label="Profit Factor" value={fmt(stats.profitFactor)} positive={stats.profitFactor >= 1} />
                <StatCard label="Expectancy" value={fmtDollar(stats.expectancy)} positive={stats.expectancy >= 0} />
                <StatCard label="Avg R-Multiple" value={fmt(stats.avgRMultiple)} positive={stats.avgRMultiple >= 0} />
                <StatCard label="Max Drawdown" value={fmtDollar(-stats.maxDrawdown)} positive={false} />
                <StatCard label="Avg Hold Time" value={fmtDuration(stats.avgHoldingSeconds)} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Card>
                  <CardHeader className="p-3 pb-1"><CardTitle className="text-sm">Best & Worst</CardTitle></CardHeader>
                  <CardContent className="p-3 pt-1 text-xs space-y-1">
                    <div className="text-green-500">Largest Win: {fmtDollar(stats.largestWin)}</div>
                    <div className="text-green-500">Avg Win: {fmtDollar(stats.avgWin)}</div>
                    <div className="text-red-500">Largest Loss: {fmtDollar(stats.largestLoss)}</div>
                    <div className="text-red-500">Avg Loss: {fmtDollar(stats.avgLoss)}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="p-3 pb-1"><CardTitle className="text-sm">Rule Config</CardTitle></CardHeader>
                  <CardContent className="p-3 pt-1 text-xs space-y-1">
                    <div>Scanner: {selectedScenario.strategySnapshot?.scannerId || selectedScenario.scannerId}</div>
                    <div>Direction: {selectedScenario.strategySnapshot?.direction || '—'}</div>
                    <div>Sizing: {selectedScenario.strategySnapshot?.sizing?.mode} {selectedScenario.strategySnapshot?.sizing?.value}%</div>
                    <div>Symbols: {selectedScenario.symbols.length > 0 ? selectedScenario.symbols.join(', ') : 'All discovered'}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Equity Curve */}
              {stats.equityCurve.length > 0 && (
                <EquityCurveChart equityCurve={stats.equityCurve} />
              )}

              {/* P&L Distribution + MFE/MAE */}
              {selectedScenario.trades.length > 0 && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                  <PnLDistributionChart trades={selectedScenario.trades.filter(t => t.status === 'Closed')} />
                  <MfeScatterPlot trades={selectedScenario.trades.filter(t => t.status === 'Closed')} />
                </div>
              )}

              {/* Trade table */}
              {selectedScenario.trades.length > 0 && (
                <Card>
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-sm">Trades ({selectedScenario.trades.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-auto max-h-[400px]">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/40 sticky top-0">
                          <tr>
                            <th className="p-2 text-left">Symbol</th>
                            <th className="p-2 text-left">Dir</th>
                            <th className="p-2 text-right">Entry</th>
                            <th className="p-2 text-right">Exit</th>
                            <th className="p-2 text-right">P&L</th>
                            <th className="p-2 text-right">R</th>
                            <th className="p-2 text-left">Reason</th>
                            <th className="p-2 text-right">Hold</th>
                            <th className="p-2 text-left">Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedScenario.trades.map(t => (
                            <tr key={t.id} className="border-b border-muted/20 hover:bg-muted/10">
                              <td className="p-2 font-medium">{t.symbol}</td>
                              <td className="p-2">{t.direction === 'Long' ? '▲' : '▼'}</td>
                              <td className="p-2 text-right">${fmt(t.entryPrice)}</td>
                              <td className="p-2 text-right">{t.exitPrice ? `$${fmt(t.exitPrice)}` : '—'}</td>
                              <td className={`p-2 text-right ${t.realizedPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {t.status === 'Closed' ? fmtDollar(t.realizedPnL) : '—'}
                              </td>
                              <td className="p-2 text-right">{t.status === 'Closed' ? fmt(t.realizedRMultiple, 1) : '—'}</td>
                              <td className="p-2">{t.exitReason || '—'}</td>
                              <td className="p-2 text-right">{t.holdingSeconds > 0 ? fmtDuration(t.holdingSeconds) : '—'}</td>
                              <td className="p-2 text-[10px] text-muted-foreground">{new Date(t.entryTime).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : selectedScenario && selectedScenario.status === 'Running' ? (
            <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Running scenario...
            </div>
          ) : selectedScenario && selectedScenario.status === 'Failed' ? (
            <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-md">
              <AlertCircle className="h-4 w-4 inline mr-1" /> {selectedScenario.error}
            </div>
          ) : (
            <div className="text-center text-muted-foreground text-sm py-12">
              Select a scenario to view results, or run a new one
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Helpers ──

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const variant = status === 'Completed' ? 'default' : status === 'Failed' ? 'destructive' : 'secondary';
  return <Badge variant={variant} className="text-[10px]">{status}</Badge>;
};

const StatCard: React.FC<{ label: string; value: string; positive?: boolean }> = ({ label, value, positive }) => (
  <Card>
    <CardContent className="p-3">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-lg font-bold ${positive === true ? 'text-green-500' : positive === false ? 'text-red-500' : ''}`}>
        {value}
      </div>
    </CardContent>
  </Card>
);
